import { useId, useEffect, useRef, useState } from 'react';

import { ComboField, ToggleField, Textarea, TextField } from '~/components/form';
import { Alert, Button, StatusChip } from '~/components/ui';
import { aiStatus } from '~/components/pages/ai/common/statusMaps';
import { apiErrorMessage } from '~/lib/apiError';
import type { ApiErrorInput } from '~/lib/apiError';
import { humanize } from '~/lib/format';
import {
  buildInitialValues,
  buildTestPayload,
  formatExecutionOutput,
  parseObjectFieldInput,
  validateRequiredFields,
  type DigitalAssistantFieldValue,
  type DigitalAssistantFormErrors,
  type DigitalAssistantFormValues,
  type DigitalAssistantInputSchema,
  type DigitalAssistantSchemaProperty,
  type DigitalAssistantTestResult,
} from '~/lib/ai/schemaForm';

export interface AssistantTesterProps {
  /** The published version's input schema — never the draft on the page. */
  schema: DigitalAssistantInputSchema;
  onTest: (
    input: Record<string, unknown>,
    userInput?: string,
  ) => Promise<DigitalAssistantTestResult>;
}

function numberFromInput(raw: string): number | undefined {
  if (raw.trim() === '') {
    return undefined;
  }
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? undefined : parsed;
}

interface TesterFieldProps {
  name: string;
  property: DigitalAssistantSchemaProperty;
  required: boolean;
  value: DigitalAssistantFieldValue;
  error?: string;
  onChange: (name: string, value: DigitalAssistantFieldValue) => void;
  onValidityChange: (name: string, isValid: boolean) => void;
}

function ObjectField({
  id,
  name,
  label,
  property,
  required,
  value,
  error,
  onChange,
  onValidityChange,
}: TesterFieldProps & { id: string; label: string }) {
  // The raw text is local so an in-progress, momentarily-invalid edit is not
  // replaced by the last parsed value on every keystroke. The parent tracks
  // validity separately and refuses to run on stale data.
  const [raw, setRaw] = useState(() => (value === undefined ? '' : JSON.stringify(value, null, 2)));
  const [invalid, setInvalid] = useState(false);

  return (
    <Textarea
      id={id}
      testId={`ai-digital-assistants-tester-${name}`}
      label={label}
      required={required}
      code
      rows={4}
      value={raw}
      placeholder='{"key": "value"}'
      helperText={property.description}
      error={invalid ? `Enter valid JSON, for example {"key": "value"}.` : error}
      onChange={(next) => {
        setRaw(next);
        const parsed = parseObjectFieldInput(next);
        setInvalid(!parsed.isValid);
        onValidityChange(name, parsed.isValid);
        if (parsed.isValid) {
          onChange(name, parsed.value);
        }
      }}
    />
  );
}

function TesterField(props: TesterFieldProps) {
  const id = useId();
  const { name, property, required, value, error, onChange, onValidityChange } = props;
  const label = property.title ?? humanize(name);

  if (property.type === 'boolean') {
    return (
      <ToggleField
        id={id}
        testId={`ai-digital-assistants-tester-${name}`}
        label={label}
        text="True"
        checked={value === true}
        helperText={property.description}
        onChange={(checked) => onChange(name, checked)}
      />
    );
  }

  if (property.type === 'object') {
    return (
      <ObjectField
        id={id}
        name={name}
        label={label}
        property={property}
        required={required}
        value={value}
        error={error}
        onChange={onChange}
        onValidityChange={onValidityChange}
      />
    );
  }

  if (property.enum && property.enum.length > 0) {
    const numeric = property.type === 'number' || property.type === 'integer';
    return (
      <ComboField
        id={id}
        testId={`ai-digital-assistants-tester-${name}`}
        label={label}
        required={required}
        placeholder="Select a value"
        helperText={property.description}
        searchable={false}
        value={value === undefined ? '' : String(value)}
        options={property.enum.map((option) => ({ value: String(option), label: String(option) }))}
        onChange={(next) =>
          onChange(name, next === '' ? undefined : numeric ? numberFromInput(next) : next)
        }
      />
    );
  }

  if (property.type === 'number' || property.type === 'integer') {
    return (
      <TextField
        id={id}
        testId={`ai-digital-assistants-tester-${name}`}
        label={label}
        required={required}
        type="number"
        step={property.type === 'integer' ? 1 : undefined}
        value={value === undefined ? '' : String(value)}
        helperText={property.description}
        error={error}
        onChange={(raw) => onChange(name, numberFromInput(raw))}
      />
    );
  }

  return (
    <TextField
      id={id}
      testId={`ai-digital-assistants-tester-${name}`}
      label={label}
      required={required}
      value={typeof value === 'string' ? value : ''}
      helperText={property.description}
      error={error}
      onChange={(next) => onChange(name, next)}
    />
  );
}

/**
 * Runs the published assistant with sample input. A response to an earlier
 * schema is discarded, so switching assistants cannot land stale output on the
 * form currently on screen.
 */
export default function AssistantTester({ schema, onTest }: AssistantTesterProps) {
  const [values, setValues] = useState<DigitalAssistantFormValues>(() =>
    buildInitialValues(schema),
  );
  const [errors, setErrors] = useState<DigitalAssistantFormErrors>({});
  // Names holding unparseable object JSON; `values` still has the last valid parse.
  const [invalidFields, setInvalidFields] = useState<Set<string>>(() => new Set());
  const [userInput, setUserInput] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<DigitalAssistantTestResult | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  // Keyed fields remount when the schema changes: an object field's raw text is
  // local state, and a same-named field must not keep the previous schema's text.
  const [generation, setGeneration] = useState(0);
  const requestRef = useRef(0);

  // Reset whenever the published schema changes (another assistant, a restore).
  // Fields are keyed by `generation`, so local raw text (object JSON) resets too.
  useEffect(() => {
    requestRef.current += 1;
    setValues(buildInitialValues(schema));
    setErrors({});
    setInvalidFields(new Set());
    setUserInput('');
    setResult(null);
    setRunError(null);
    setIsRunning(false);
    setGeneration((previous) => previous + 1);
  }, [schema]);

  const change = (name: string, value: DigitalAssistantFieldValue) => {
    setValues((previous) => ({ ...previous, [name]: value }));
    setErrors((previous) => {
      if (!previous[name]) {
        return previous;
      }
      const next = { ...previous };
      delete next[name];
      return next;
    });
  };

  const changeValidity = (name: string, isValid: boolean) => {
    setInvalidFields((previous) => {
      if (previous.has(name) === !isValid) {
        return previous;
      }
      const next = new Set(previous);
      if (isValid) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const run = async () => {
    if (isRunning) {
      return;
    }

    const validation = validateRequiredFields(schema, values);
    for (const name of invalidFields) {
      validation[name] = 'Enter valid JSON before running the test.';
    }
    setErrors(validation);
    if (Object.keys(validation).length > 0) {
      return;
    }

    const requestId = ++requestRef.current;
    const extra = userInput.trim();
    setIsRunning(true);
    setResult(null);
    setRunError(null);

    try {
      const response = await onTest(buildTestPayload(values), extra || undefined);
      if (requestRef.current === requestId) {
        setResult(response);
      }
    } catch (error) {
      if (requestRef.current === requestId) {
        setRunError(
          apiErrorMessage(error as ApiErrorInput) ??
            'The test could not run. Check the assistant and try again.',
        );
      }
    } finally {
      if (requestRef.current === requestId) {
        setIsRunning(false);
      }
    }
  };

  const fieldNames = Object.keys(schema.properties);
  const requiredFields = new Set(schema.required ?? []);

  return (
    <div className="space-y-5">
      <p className="text-xs text-gray-500 dark:text-gray-400">
        This runs the last published version. Unsaved changes on this page are not used.
      </p>

      {fieldNames.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          This assistant defines no input fields, so the test runs with the additional instructions
          alone.
        </p>
      ) : (
        <div className="space-y-4">
          {fieldNames.map((name) => (
            <TesterField
              key={`${generation}:${name}`}
              name={name}
              property={schema.properties[name]}
              required={requiredFields.has(name)}
              value={values[name]}
              error={errors[name]}
              onChange={change}
              onValidityChange={changeValidity}
            />
          ))}
        </div>
      )}

      <Textarea
        id="ai-digital-assistants-tester-instructions"
        testId="ai-digital-assistants-tester-instructions"
        label="Additional instructions"
        rows={2}
        value={userInput}
        onChange={setUserInput}
        placeholder="Optional supplementary instructions for this run"
        helperText="Sent with the fields above as extra context. Leave it blank to test the prompt alone."
      />

      <Button
        onClick={() => void run()}
        loading={isRunning}
        testId="ai-digital-assistants-tester-run"
      >
        {isRunning ? 'Running…' : 'Run test'}
      </Button>

      {runError ? (
        <Alert variant="error">
          <p className="text-sm text-gray-800 dark:text-white/90">{runError}</p>
        </Alert>
      ) : null}

      {result ? (
        <div className="space-y-3" data-testid="ai-digital-assistants-tester-result">
          <div className="flex items-center gap-2">
            <StatusChip {...aiStatus(result.success ? 'completed' : 'failed')} />
            <span className="text-sm font-medium text-gray-800 dark:text-white/90">Result</span>
          </div>
          <pre className="overflow-x-auto whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800 dark:border-gray-800 dark:bg-white/[0.03] dark:text-gray-200">
            {formatExecutionOutput(result)}
          </pre>

          {result.errors && result.errors.length > 0 ? (
            <Alert variant="warning">
              {result.errors.map((executionError, index) => (
                <p
                  key={`${executionError.code}-${index}`}
                  className="text-sm text-gray-800 dark:text-white/90"
                >
                  {executionError.message}
                </p>
              ))}
            </Alert>
          ) : null}

          {result.usage ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {[
                result.usage.inputTokens !== undefined
                  ? `${result.usage.inputTokens} input tokens`
                  : null,
                result.usage.outputTokens !== undefined
                  ? `${result.usage.outputTokens} output tokens`
                  : null,
                result.usage.totalTokens !== undefined
                  ? `${result.usage.totalTokens} total tokens`
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          ) : null}

          {result.tokenLimits ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              User: {result.tokenLimits.user.remaining} of {result.tokenLimits.user.total} tokens
              remaining ({result.tokenLimits.user.remainingPercent}%). Platform:{' '}
              {result.tokenLimits.platform.remaining} of {result.tokenLimits.platform.total}{' '}
              remaining ({result.tokenLimits.platform.remainingPercent}%).
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
